/**
 * PersonFormFields — shared person field set (#1002)
 *
 * Single source for the person form fields and their validation, used by
 * PersonFormDialog and the inline person creation in the work-order create
 * flow. `showExtended=false` renders the essentials (name, phone, email)
 * plus a collapsed "Lisätiedot" toggle for address and notes (#2904).
 * WhatsApp and language stay on the full person form.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Collapse, MenuItem, Stack, TextField } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { TFunction } from 'i18next';
import { languageNames, supportedLanguages } from '@/lib/i18n';
import { LaborRateSelect } from '@/components/common/LaborRateSelect';

export interface PersonFieldsData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  invoiceEmail: string;
  whatsappNumber: string;
  preferredLanguage: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  notes: string;
  /** Omitted on legacy wizard drafts that never collected a named rate. */
  laborRateId?: string;
}

export const initialPersonFieldsData: PersonFieldsData = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  invoiceEmail: '',
  whatsappNumber: '',
  preferredLanguage: 'fi',
  streetAddress: '',
  postalCode: '',
  city: '',
  notes: '',
  laborRateId: '',
};

export function validatePersonFields(
  data: PersonFieldsData,
  t: TFunction,
): Partial<Record<keyof PersonFieldsData, string>> {
  const errors: Partial<Record<keyof PersonFieldsData, string>> = {};
  if (!data.firstName.trim()) errors.firstName = t('validation.firstNameRequired');
  if (!data.lastName.trim()) errors.lastName = t('validation.lastNameRequired');
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = t('validation.emailInvalid');
  }
  if (data.invoiceEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.invoiceEmail.trim())) {
    errors.invoiceEmail = t('validation.emailInvalid');
  }
  return errors;
}

/** Build the create/update payload from the field state (trims, empty → null). */
export function personFieldsToPayload(data: PersonFieldsData) {
  return {
    firstName: data.firstName.trim(),
    lastName: data.lastName.trim(),
    phone: data.phone.trim() || null,
    email: data.email.trim() || null,
    invoiceEmail: data.invoiceEmail.trim() || null,
    whatsappNumber: data.whatsappNumber.trim() || null,
    preferredLanguage: data.preferredLanguage,
    streetAddress: data.streetAddress.trim() || null,
    postalCode: data.postalCode.trim() || null,
    city: data.city.trim() || null,
    notes: data.notes.trim() || null,
    ...(data.laborRateId === undefined
      ? {}
      : { laborRateId: data.laborRateId.trim() || null }),
  };
}

interface PersonFormFieldsProps {
  data: PersonFieldsData;
  errors: Partial<Record<keyof PersonFieldsData, string>>;
  onChange: (field: keyof PersonFieldsData, value: string) => void;
  /** Render the full field set (dialog) or only the essentials (inline create). */
  showExtended?: boolean;
  autoFocus?: boolean;
}

function AddressAndNotesFields({
  data,
  onChange,
}: {
  data: PersonFieldsData;
  onChange: (field: keyof PersonFieldsData, value: string) => void;
}) {
  const { t } = useTranslation('persons');

  return (
    <>
      <TextField
        label={t('fields.streetAddress')}
        value={data.streetAddress}
        onChange={(e) => onChange('streetAddress', e.target.value)}
        fullWidth
      />
      <Stack direction="row" spacing={2}>
        <TextField
          label={t('fields.postalCode')}
          value={data.postalCode}
          onChange={(e) => onChange('postalCode', e.target.value)}
          sx={{ width: '40%' }}
        />
        <TextField
          label={t('fields.city')}
          value={data.city}
          onChange={(e) => onChange('city', e.target.value)}
          fullWidth
        />
      </Stack>
      <TextField
        label={t('fields.notes')}
        value={data.notes}
        onChange={(e) => onChange('notes', e.target.value)}
        fullWidth
        multiline
        rows={3}
      />
    </>
  );
}

export function PersonFormFields({
  data,
  errors,
  onChange,
  showExtended = true,
  autoFocus,
}: PersonFormFieldsProps) {
  const { t } = useTranslation('persons');
  const [showAdditional, setShowAdditional] = useState(false);

  return (
    <>
      <TextField
        label={t('fields.firstName')}
        value={data.firstName}
        onChange={(e) => onChange('firstName', e.target.value)}
        error={!!errors.firstName}
        helperText={errors.firstName}
        fullWidth
        required
        autoFocus={autoFocus}
      />
      <TextField
        label={t('fields.lastName')}
        value={data.lastName}
        onChange={(e) => onChange('lastName', e.target.value)}
        error={!!errors.lastName}
        helperText={errors.lastName}
        fullWidth
        required
      />
      <TextField
        label={t('fields.phone')}
        value={data.phone}
        onChange={(e) => onChange('phone', e.target.value)}
        fullWidth
      />
      <TextField
        label={t('fields.email')}
        value={data.email}
        onChange={(e) => onChange('email', e.target.value)}
        error={!!errors.email}
        helperText={errors.email}
        type="email"
        fullWidth
      />
      {showExtended ? (
        <>
          <TextField
            label={t('fields.invoiceEmail')}
            value={data.invoiceEmail}
            onChange={(e) => onChange('invoiceEmail', e.target.value)}
            error={!!errors.invoiceEmail}
            helperText={errors.invoiceEmail ?? t('invoiceEmailHelp')}
            type="email"
            fullWidth
          />
          <TextField
            label={t('fields.whatsappNumber')}
            value={data.whatsappNumber}
            onChange={(e) => onChange('whatsappNumber', e.target.value)}
            fullWidth
          />
          <TextField
            select
            label={t('fields.preferredLanguage')}
            value={data.preferredLanguage}
            onChange={(e) => onChange('preferredLanguage', e.target.value)}
            fullWidth
          >
            {supportedLanguages.map((lng) => (
              <MenuItem key={lng} value={lng}>
                {languageNames[lng]}
              </MenuItem>
            ))}
          </TextField>
          <LaborRateSelect
            value={data.laborRateId || null}
            onChange={(id) => onChange('laborRateId', id ?? '')}
            label={t('fields.laborRate')}
            helperText={t('fields.laborRateHint')}
            emptyLabel={t('fields.laborRateWorkshopDefault')}
          />
          <AddressAndNotesFields data={data} onChange={onChange} />
        </>
      ) : (
        <Box>
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={() => setShowAdditional((open) => !open)}
            endIcon={showAdditional ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            aria-expanded={showAdditional}
            aria-controls="person-additional-details"
            sx={{ alignSelf: 'flex-start', ml: -1 }}
          >
            {t('additionalDetails')}
          </Button>
          <Collapse in={showAdditional} unmountOnExit>
            <Stack id="person-additional-details" spacing={3} sx={{ mt: 2 }}>
              <AddressAndNotesFields data={data} onChange={onChange} />
            </Stack>
          </Collapse>
        </Box>
      )}
    </>
  );
}
